<template>
  <div class="file-upload-wrapper">
    <!-- Encabezado de sección -->
    <div class="page-header mb-6">
      <div class="page-header-icon">
        <v-icon color="white" size="28">mdi-file-excel</v-icon>
      </div>
      <div>
        <h2 class="page-title">Cargar Listado de Premios</h2>
        <p class="page-subtitle">Sube un archivo Excel (.xlsx) para procesar el listado de premios</p>
      </div>
    </div>

    <!-- Card de carga -->
    <div class="upload-card-container">
      <v-card class="upload-card elevation-3">
        <v-card-text class="pa-10">

          <!-- Zona de Drop / Click -->
          <div
            class="upload-zone"
            :class="{ 'upload-zone--dragging': isDragging, 'upload-zone--has-file': !!selectedFile }"
            @click="triggerFileInput"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <input
              type="file"
              ref="fileInput"
              class="d-none"
              accept=".xlsx"
              @change="handleFileChange"
            >

            <!-- Sin archivo seleccionado -->
            <div v-if="!selectedFile" class="upload-zone-content">
              <div class="upload-icon-wrapper mb-5">
                <v-icon size="56" color="#294697">mdi-cloud-upload-outline</v-icon>
              </div>
              <h3 class="upload-title mb-2">Haz clic para seleccionar un archivo</h3>
              <p class="upload-subtitle mb-4">o arrastra y suelta aquí tu archivo</p>
              <v-chip outlined color="#294697" small class="px-4">
                <v-icon left small>mdi-file-excel</v-icon>
                Solo archivos .xlsx
              </v-chip>
            </div>

            <!-- Archivo seleccionado -->
            <div v-else class="upload-zone-content">
              <div class="file-selected-icon mb-4">
                <v-icon size="56" color="#22c55e">mdi-file-check-outline</v-icon>
              </div>
              <h3 class="upload-title mb-2" style="color: #166534;">Archivo listo para subir</h3>
              <v-chip
                label
                color="#294697"
                text-color="white"
                class="px-4 py-2 mb-4"
              >
                <v-icon left small>mdi-microsoft-excel</v-icon>
                {{ selectedFile.name }}
              </v-chip>
              <div>
                <v-btn text color="red darken-1" small @click.stop="clearFile">
                  <v-icon left small>mdi-close-circle</v-icon>
                  Cambiar archivo
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Botón procesar -->
          <v-btn
            block
            large
            color="#294697"
            dark
            class="mt-6 upload-btn"
            :disabled="!selectedFile"
            :loading="isUploading"
            @click="uploadFile"
            height="52"
          >
            <v-icon left>mdi-send</v-icon>
            PROCESAR ARCHIVO
          </v-btn>

        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import env from "../../env";
import Swal from "sweetalert2";

export default {
  name: "FileUpload",
  data() {
    return {
      selectedFile: null,
      isDragging: false,
      isUploading: false,
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    validateAndSetFile(file) {
      const extension = file.name.split(".").pop().toLowerCase();
      if (extension !== "xlsx") {
        Swal.fire({
          icon: "error",
          title: "Archivo no válido",
          text: "Solo se permiten archivos con extensión .xlsx",
          confirmButtonColor: "#294697",
        });
        this.clearFile();
        return;
      }
      this.selectedFile = file;
    },
    clearFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    async uploadFile() {
      if (!this.selectedFile) return;

      this.isUploading = true;

      const formData = new FormData();
      formData.append("archivo", this.selectedFile);
      formData.append("idUsuario", localStorage.getItem("id"));
      const idEmpresa = localStorage.getItem("idEmpresa");
      formData.append("id_empresa", idEmpresa != null ? String(idEmpresa) : "");

      try {
        const response = await axios.post(
          `${env.endpoint}premios/subir_archivo`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (
          response.data.status === true ||
          response.data.status === "true" ||
          response.data.estado ||
          response.data.success
        ) {
          Swal.fire({
            icon: "success",
            title: "¡Éxito!",
            text: response.data.mensaje || "El archivo se ha procesado correctamente.",
            confirmButtonColor: "#294697",
          });
          this.clearFile();
        } else {
          throw new Error(response.data.mensaje || "Error al procesar el archivo.");
        }
      } catch (error) {
        console.error("Upload error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "No se pudo subir el archivo. Intenta de nuevo.",
          confirmButtonColor: "#294697",
        });
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

/* ─── Wrapper principal ──────────────────────────────────────────── */
.file-upload-wrapper {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center; /* Centrado horizontal real */
  padding: 0;
}

/* ─── Encabezado de sección ─────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-header-icon {
  width: 52px;
  height: 52px;
  min-width: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #294697, #1D3C7F);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(41, 70, 151, 0.35);
}

.page-title {
  font-family: 'Poppins', sans-serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 2px 0 0;
}

/* ─── Contenedor de la card: centra horizontalmente ─────────────── */
.upload-card-container {
  display: flex;
  justify-content: center;
}

/* ─── Card principal ─────────────────────────────────────────────── */
.upload-card {
  border-radius: 20px !important;
  overflow: hidden;
  background-color: #ffffff;
  width: 100%;
  max-width: 560px;
}

/* ─── Zona de drag & drop ────────────────────────────────────────── */
.upload-zone {
  border: 2.5px dashed rgba(41, 70, 151, 0.25);
  border-radius: 14px;
  padding: 48px 32px;
  background-color: rgba(41, 70, 151, 0.025);
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  text-align: center;
}

.upload-zone:hover {
  border-color: #294697;
  background-color: rgba(41, 70, 151, 0.05);
  transform: translateY(-2px);
}

.upload-zone--dragging {
  border-color: #294697;
  background-color: rgba(41, 70, 151, 0.08);
  box-shadow: 0 0 0 4px rgba(41, 70, 151, 0.12);
  transform: scale(1.01);
}

.upload-zone--has-file {
  border-color: #22c55e;
  background-color: rgba(34, 197, 94, 0.04);
}

.upload-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ─── Icono circular ─────────────────────────────────────────────── */
.upload-icon-wrapper {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: rgba(41, 70, 151, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.upload-zone:hover .upload-icon-wrapper {
  background: rgba(41, 70, 151, 0.14);
}

/* ─── Textos ─────────────────────────────────────────────────────── */
.upload-title {
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.upload-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

/* ─── Botón procesar ─────────────────────────────────────────────── */
.upload-btn {
  border-radius: 12px !important;
  font-family: 'Poppins', sans-serif !important;
  font-weight: 600 !important;
  letter-spacing: 1.2px !important;
  font-size: 0.9rem !important;
  box-shadow: 0 4px 16px rgba(41, 70, 151, 0.3) !important;
  transition: all 0.2s ease !important;
}

.upload-btn:not([disabled]):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(41, 70, 151, 0.4) !important;
}

/* ─── Responsive laptop (hasta 1366px) ──────────────────────────── */
@media (max-width: 1366px) {
  .file-upload-wrapper {
    min-height: calc(100vh - 110px);
  }

  .upload-zone {
    padding: 36px 24px;
    min-height: 200px;
  }

  .upload-icon-wrapper {
    width: 80px;
    height: 80px;
  }

  .upload-title {
    font-size: 1rem;
  }
}
</style>